import { useCallback, useEffect, useState } from 'react'
import {
  readCalendarTaskDetailsPreference,
  writeCalendarTaskDetailsPreference,
} from '@/shared/lib/calendarTaskDetailsPreference'

const TASK_FREQUENCY_DETAILS_CHANGE_EVENT = 'ell:calendar-task-frequency-details-change'

interface TaskFrequencyDetailsChange {
  tenantId: number | null | undefined
  userId: number | null | undefined
  showTaskFrequencyDetails: boolean
}

// One tenant/user preference drives every mounted calendar panel (Dashboard can show several),
// so changes are broadcast in-page; the event also covers storage-write failures.
export const useTaskFrequencyDetailsPreference = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): [boolean, (showTaskFrequencyDetails: boolean) => void] => {
  const [showTaskFrequencyDetails, setShowTaskFrequencyDetails] = useState(() =>
    readCalendarTaskDetailsPreference(tenantId, userId),
  )

  useEffect(() => {
    setShowTaskFrequencyDetails(readCalendarTaskDetailsPreference(tenantId, userId))

    const handlePreferenceChange = (event: Event) => {
      const { detail } = event as CustomEvent<TaskFrequencyDetailsChange>
      if (detail.tenantId !== tenantId || detail.userId !== userId) return

      setShowTaskFrequencyDetails(detail.showTaskFrequencyDetails)
    }

    window.addEventListener(TASK_FREQUENCY_DETAILS_CHANGE_EVENT, handlePreferenceChange)
    return () =>
      window.removeEventListener(TASK_FREQUENCY_DETAILS_CHANGE_EVENT, handlePreferenceChange)
  }, [tenantId, userId])

  const updateShowTaskFrequencyDetails = useCallback(
    (nextShowTaskFrequencyDetails: boolean) => {
      setShowTaskFrequencyDetails(nextShowTaskFrequencyDetails)
      writeCalendarTaskDetailsPreference(tenantId, userId, nextShowTaskFrequencyDetails)
      window.dispatchEvent(
        new CustomEvent<TaskFrequencyDetailsChange>(TASK_FREQUENCY_DETAILS_CHANGE_EVENT, {
          detail: { tenantId, userId, showTaskFrequencyDetails: nextShowTaskFrequencyDetails },
        }),
      )
    },
    [tenantId, userId],
  )

  return [showTaskFrequencyDetails, updateShowTaskFrequencyDetails]
}
